import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sms, contacts } from '../services/api';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import ContactsTab from './ContactsTab';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { Tooltip } from 'react-tooltip';
import { useHotkeys } from 'react-hotkeys-hook';
import SMSUsageBar from './SMSUsageBar';
import EmojiPicker from 'emoji-picker-react';

// Create audio element for notifications
const notificationSound = new Audio('/notification.mp3');

const ChatWindow = () => {
  const { user, token, setUser, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messageInputRef = useRef(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('messages');
  const [newMessage, setNewMessage] = useState('');
  // Staged MMS attachments: uploaded and ready to send with the next message
  const [pendingMedia, setPendingMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [socketConnected, setSocketConnected] = useState(false);
  const [notificationsMuted, setNotificationsMuted] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const messageContainerRef = useRef(null);
  const MESSAGES_PER_PAGE = 50;
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [scrollPreservationActive, setScrollPreservationActive] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const processedMessages = useRef(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  const searchInputRef = useRef(null);
  const [userContacts, setUserContacts] = useState({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContactNumber, setSelectedContactNumber] = useState(null);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showDeleteConversationConfirm, setShowDeleteConversationConfirm] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Utility function to normalize phone numbers for consistent comparison
  const normalizeNumber = (number) => {
    if (!number) return '';
    // Remove all non-digits
    let normalized = number.replace(/\D/g, '');
    // Remove leading '1' if present for US numbers (10-digit format)
    if (normalized.startsWith('1') && normalized.length === 11) {
      normalized = normalized.substring(1);
    }
    return normalized;
  };

  // Show/hide keyboard shortcuts modal
  useHotkeys('shift+/', () => {
    setShowKeyboardShortcuts(true);
  });

  useHotkeys('esc', () => {
    if (showKeyboardShortcuts) {
      setShowKeyboardShortcuts(false);
      return;
    }
    if (document.activeElement === searchInputRef.current) {
      searchInputRef.current.blur();
      setSearchQuery('');
    }
  }, [showKeyboardShortcuts]);

  // Keyboard shortcuts
  useHotkeys('ctrl+k, cmd+k', (e) => {
    e.preventDefault();
    searchInputRef.current?.focus();
  });


  useHotkeys('ctrl+backspace, cmd+backspace', (e) => {
    if (document.activeElement === searchInputRef.current) {
      e.preventDefault();
      setSearchQuery('');
    }
  });

  useHotkeys('ctrl+n, cmd+n', (e) => {
    e.preventDefault();
    toggleNotifications();
  });

  useHotkeys('ctrl+t, cmd+t', (e) => {
    e.preventDefault();
    setShowNewConversation(true);
  });

  // Tab switching shortcuts
  useHotkeys('ctrl+1, cmd+1', (e) => {
    e.preventDefault();
    setActiveTab('messages');
  });

  useHotkeys('ctrl+2, cmd+2', (e) => {
    e.preventDefault();
    setActiveTab('contacts');
  });

  // Conversation navigation shortcuts
  useHotkeys('up, down', (e) => {
    if (activeTab !== 'messages' || showNewConversation) return;
    
    e.preventDefault();
    const currentIndex = filteredConversations.findIndex(
      conv => normalizeNumber(conv.with) === normalizeNumber(selectedConversation)
    );
    
    let nextIndex;
    if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : filteredConversations.length - 1;
    } else {
      nextIndex = currentIndex < filteredConversations.length - 1 ? currentIndex + 1 : 0;
    }
    
    const nextConversation = filteredConversations[nextIndex];
    if (nextConversation) {
      loadMessages(nextConversation.with);
    }
  });

  // Filter conversations based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
      return;
    }

    const filtered = conversations.filter(conv => 
      formatPhoneNumber(conv.with).toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.lastMessage?.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredConversations(filtered);
  }, [searchQuery, conversations]);

  // ====== UTILITY FUNCTIONS ======
  
  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      notificationSound.play();
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  };

  // Toggle notifications
  const toggleNotifications = () => {
    setNotificationsMuted(!notificationsMuted);
    localStorage.setItem('notificationsMuted', (!notificationsMuted).toString());
  };
  
  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    const cleaned = normalizeNumber(phoneNumber);
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumber;
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Time only format
    const timeFormat = { hour: '2-digit', minute: '2-digit' };
    const time = date.toLocaleTimeString([], timeFormat);
    
    // If message is from today, show only time
    if (date.toDateString() === now.toDateString()) {
      return time;
    }
    
    // If message is from yesterday, show "Yesterday" and time
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${time}`;
    }
    
    // For older messages, show Month Day Year
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    return `${month} ${day}, ${year} ${time}`;
  };

  // ====== SCROLL MANAGEMENT ======
  
  // Check if we're at the bottom of the scroll container
  const checkIfAtBottom = useCallback(() => {
    if (!messageContainerRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
    // Consider "at bottom" if within 100px of bottom or if container is smaller than viewport
    return scrollHeight <= clientHeight || (scrollHeight - scrollTop - clientHeight) < 100;
  }, []);

  // Scroll to bottom with optional smooth behavior
  const scrollToBottom = useCallback((smooth = true) => {
    if (!messageContainerRef.current) return;
    
    requestAnimationFrame(() => {
      if (messageContainerRef.current) {
        const options = smooth ? { behavior: 'smooth' } : undefined;
        const scrollHeight = messageContainerRef.current.scrollHeight;
        
        console.log('Scrolling to bottom, height:', scrollHeight);
        
        messageContainerRef.current.scrollTo({
          top: scrollHeight,
          ...options
        });
        
        // Update our internal state
        setIsAtBottom(true);
        setShowNewMessageIndicator(false);
        setUnreadCount(0);
      }
    });
  }, []);

  // For handling scroll events
  const handleScroll = useCallback(() => {
    if (!messageContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
    
    // Save current scroll position for reference
    setScrollPosition(scrollTop);
    
    // Update isAtBottom state
    const isScrolledToBottom = scrollHeight <= clientHeight || (scrollHeight - scrollTop - clientHeight) < 100;
    setIsAtBottom(isScrolledToBottom);
    
    // Hide indicator when scrolled to bottom
    if (isScrolledToBottom && showNewMessageIndicator) {
      setShowNewMessageIndicator(false);
      setUnreadCount(0);
    }
    
    // Check if we need to load more messages when scrolling to top
    if (scrollTop === 0 && hasMore && !messagesLoading && !scrollPreservationActive) {
      console.log('Loading more messages due to scroll');
      // Call loadMoreMessages, which will be defined below
      // We don't include it in the dependency array to avoid circular references
      loadMoreMessages();
    }
  }, [showNewMessageIndicator, hasMore, messagesLoading, scrollPreservationActive]);
  
  // Throttled scroll handler to prevent too many events
  const throttledScrollHandler = useCallback(() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleScroll();
      }, 100);
    };
  }, [handleScroll]);
  
  // ====== API FUNCTIONS ======

  // Load conversations list
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check if user has a DID assigned
      if (!user?.assignedDID) {
        setConversations([]);
        toast.warning('No phone number assigned to your account. Please contact your administrator.');
        return;
      }
      
      const response = await sms.getConversations();
      if (response.data) {
        // Ensure numbers are normalized for consistency
        const normalizedConversations = response.data.map(conv => ({
          ...conv,
          with: normalizeNumber(conv.with),
          unreadCount: conv.unreadCount || 0 // Use server-provided unreadCount or default to 0
        }));
        
        // Sort conversations by timestamp
        const sortedConversations = normalizedConversations.sort((a, b) => 
          new Date(b.timestamp || b.lastMessage?.timestamp || 0) - 
          new Date(a.timestamp || a.lastMessage?.timestamp || 0)
        );
        
        console.log('Normalized and sorted conversations:', sortedConversations);
        setConversations(sortedConversations);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load initial messages for conversation
  const loadMessages = useCallback(async (partner) => {
    try {
      const normalizedPartner = normalizeNumber(partner);
      console.log('Loading messages for partner:', normalizedPartner);
      setMessagesLoading(true);
      setSelectedConversation(normalizedPartner);
      setShowNewConversation(false);
      setActiveTab('messages');
      setPage(1);
      setShowNewMessageIndicator(false);
      setUnreadCount(0);
      
      // Reset unread count for this conversation in the conversations list immediately
      setConversations(prevConversations => {
        const updatedConversations = prevConversations.map(conv => {
          // Compare normalized numbers for matching
          if (normalizeNumber(conv.with) === normalizedPartner) {
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
        return updatedConversations;
      });
      
      // Mark messages as read immediately when loading conversation
      try {
        await sms.markAsRead(normalizedPartner);
        console.log('Marked messages as read for conversation:', normalizedPartner);
      } catch (markReadError) {
        console.error('Error marking messages as read:', markReadError);
      }
      
      const response = await sms.getConversation(normalizedPartner);
      console.log('Received message response:', {
        messageCount: response.data?.messages?.length,
        hasMore: response.data?.pagination?.hasMore,
        unreadCount: response.data?.unreadCount
      });
      
      if (response.data?.messages) {
        const sortedMessages = response.data.messages.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        
        setMessages(sortedMessages);
        setHasMore(response.data.pagination.hasMore);
        
        // Update processed messages set to prevent duplicates
        sortedMessages.forEach(msg => {
          if (msg._id) {
            processedMessages.current.add(msg._id);
          }
        });
      } else {
        setMessages([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
      setMessages([]);
      setHasMore(false);
    } finally {
      setMessagesLoading(false);
    }
  }, [normalizeNumber]);

  // LOAD MORE MESSAGES - With scroll position preservation
  const loadMoreMessages = useCallback(async () => {
    if (!hasMore || messagesLoading || !selectedConversation) return;
    
    try {
      setMessagesLoading(true);
      setScrollPreservationActive(true);
      
      // Remember height before loading more messages
      const scrollContainer = messageContainerRef.current;
      const prevScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
      const prevScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
      
      const nextPage = page + 1;
      const response = await sms.getConversation(selectedConversation, nextPage, MESSAGES_PER_PAGE);
      
      if (response.data?.messages && response.data.messages.length > 0) {
        setMessages(prevMessages => {
          const newMessages = [...response.data.messages, ...prevMessages]
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          return newMessages;
        });
        setPage(nextPage);
        setHasMore(response.data.pagination.hasMore);
        
        // After state updates and re-render, restore scroll position
        setTimeout(() => {
          if (messageContainerRef.current) {
            // Calculate new position to maintain the same visual position
            const newScrollHeight = messageContainerRef.current.scrollHeight;
            const heightDifference = newScrollHeight - prevScrollHeight;
            const newScrollTop = prevScrollTop + heightDifference;
            
            console.log('Preserving scroll position:', {
              prevHeight: prevScrollHeight,
              newHeight: newScrollHeight,
              prevTop: prevScrollTop,
              newTop: newScrollTop
            });
            
            messageContainerRef.current.scrollTop = newScrollTop;
          }
          setScrollPreservationActive(false);
        }, 50);
      } else {
        setHasMore(false);
        setScrollPreservationActive(false);
      }
    } catch (error) {
      console.error('Error loading more messages:', error);
      toast.error('Failed to load more messages');
      setScrollPreservationActive(false);
    } finally {
      setMessagesLoading(false);
    }
  }, [hasMore, messagesLoading, selectedConversation, page, MESSAGES_PER_PAGE]);

  // Handle SMS limit error
  const handleSmsLimitError = useCallback((error, tempMessageId) => {
    const { limit, used, nextReset } = error.response.data;
    const resetDate = new Date(nextReset).toLocaleDateString();
    
    toast.error(
      `Monthly SMS limit reached (${used}/${limit} messages used). Limit resets on ${resetDate}.`, 
      { autoClose: false }
    );
    
    setMessages(prevMessages => {
      return prevMessages.map(msg => 
        msg._id === tempMessageId ? { 
          ...msg, 
          status: 'failed',
          error: 'SMS limit reached'
        } : msg
      );
    });
    
    // Update user state with latest usage info
    setUser(prevUser => ({
      ...prevUser,
      smsUsage: { monthly: used },
      smsLimit: limit,
      smsResetDate: nextReset
    }));
  }, [setUser, setMessages, toast]); // Added toast dependency

  // Handle sending a message
  // The same message can reach the list three ways: the optimistic bubble, the
  // send response, and the socket broadcast (which now also reaches the sender,
  // since events fan out to the whole organization). Collapse them by id,
  // preferring the server's copy over any temporary one.
  const dedupeMessages = (list) => {
    const byId = new Map();
    const out = [];
    for (const msg of list) {
      const id = msg && msg._id ? String(msg._id) : null;
      if (!id) { out.push(msg); continue; }
      if (byId.has(id)) {
        const idx = byId.get(id);
        // Keep whichever copy came from the server.
        if (out[idx].isTemp && !msg.isTemp) out[idx] = msg;
        continue;
      }
      byId.set(id, out.length);
      out.push(msg);
    }
    return out;
  };

  // Upload picked files straight away so the send itself is fast and the user
  // gets an immediate preview.
  const handleFilesPicked = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;

    try {
      setUploadingMedia(true);
      setUploadProgress(0);
      const response = await sms.uploadMedia(files, setUploadProgress);
      const uploaded = response?.data?.media || [];
      setPendingMedia(prev => [...prev, ...uploaded]);
      if (uploaded.length) {
        toast.success(`${uploaded.length} attachment${uploaded.length > 1 ? 's' : ''} ready`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Could not upload attachment';
      toast.error(msg);
    } finally {
      setUploadingMedia(false);
      setUploadProgress(0);
      // Reset so picking the same file again still fires onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePendingMedia = (index) => {
    setPendingMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = useCallback(async (e) => { // Add handleSmsLimitError to dependencies
    e.preventDefault();
    
    // An MMS may carry only media, so allow an empty caption when something
    // is attached.
    if ((!newMessage.trim() && pendingMedia.length === 0) || !selectedConversation) return;

    const mediaToSend = pendingMedia;

    // Create a temporary message object
    const tempMessage = {
      _id: `temp-${Date.now()}`,
      content: newMessage,
      media: mediaToSend,
      messageType: mediaToSend.length ? 'mms' : 'sms',
      from: user?.assignedDID?.number,
      to: selectedConversation,
      timestamp: new Date().toISOString(),
      direction: 'outbound',
      status: 'sending',
      sender: user?._id,
      senderUsername: user?.username,
      isTemp: true
    };

    try {
      setIsSending(true);
      
      // Add the temporary message to the UI
      setMessages(prevMessages => {
        return [...prevMessages, tempMessage];
      });
      
      // Clear the input and staged attachments immediately
      setNewMessage('');
      setPendingMedia([]);

      // Send the message to the API
      const response = await sms.send({
        to: selectedConversation,
        content: newMessage,
        media: mediaToSend
      });

      // The server returns the message directly in response.data
      // But sometimes it might return it in response.data.message format
      const messageData = response?.data?.message || response?.data;
      
      if (messageData && messageData._id) {
        console.log('Message sent successfully, updating UI with server response');
        
        try {
          // Use a function to update messages to avoid the r is not a function error
          setMessages(prevMessages => dedupeMessages(
            prevMessages.map(msg =>
              msg._id === tempMessage._id ? messageData : msg
            )
          ));

          // Update user state with latest usage info if provided
          if (response.data?.smsUsage) {
            setUser(prevUser => {
              if (!prevUser) return prevUser;
              
              const updatedUser = {
                ...prevUser,
                smsUsage: { monthly: response.data.smsUsage.used },
                smsLimit: response.data.smsUsage.limit,
                smsResetDate: response.data.smsUsage.nextReset,
                smsStartDate: response.data.smsUsage.startDate || prevUser.smsStartDate
              };
              
              return updatedUser;
            });
          }
        } catch (stateUpdateError) {
          console.error('Error updating state after successful message send:', stateUpdateError);
        }
      } else {
        // Response exists but no message data - keep the temp message but mark as sent
        console.log('Response received but no proper message data, keeping temp message');
        
        try {
          // Use a function to update messages to avoid the r is not a function error
          setMessages(prevMessages => dedupeMessages(
            prevMessages.map(msg =>
              msg._id === tempMessage._id
                ? { ...msg, ...(messageData || {}), status: messageData?.status || 'sent', isTemp: false }
                : msg
            )
          ));
        } catch (stateUpdateError) {
          console.error('Error updating message status:', stateUpdateError);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      console.log('Error details:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message
      });

      if (error?.response?.status === 403 && error?.response?.data?.message?.includes('SMS limit reached')) {
        handleSmsLimitError(error, tempMessage._id);
      } else if (error?.response?.status !== 500) {
        // For non-server errors that aren't limit related
        toast.error(error?.response?.data?.message || 'Failed to send message');
        
        try {
          setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(msg => 
              msg._id === tempMessage._id ? { ...msg, status: 'failed' } : msg
            );
            return updatedMessages;
          });
        } catch (stateUpdateError) {
          console.error('Error updating message status:', stateUpdateError);
        }
      } else {
        // For server errors
        toast.error('Server error - please try again later');
        
        try {
          setMessages(prevMessages => {
            const updatedMessages = prevMessages.map(msg => 
              msg._id === tempMessage._id ? { ...msg, status: 'failed' } : msg
            );
            return updatedMessages;
          });
        } catch (stateUpdateError) {
          console.error('Error updating message status:', stateUpdateError);
        }
      }
    } finally {
      setIsSending(false);
    }
  }, [newMessage, selectedConversation, user, setUser, handleSmsLimitError]);

  // Start a new conversation
  const startNewConversation = useCallback(async (e) => {
    e.preventDefault();
    
    if (!newPhoneNumber.trim() || !newMessage.trim()) return;
    
    // Format phone number - ensure consistent format
    let formattedNumber = normalizeNumber(newPhoneNumber.trim());
    
    // Add country code for API calls (server will normalize it again)
    // This maintains API compatibility while ensuring our internal representation is normalized
    let apiNumber = formattedNumber;
    if (!apiNumber.startsWith('1') && apiNumber.length === 10) {
      apiNumber = '1' + apiNumber;
    }

    // Create a temporary message
    const tempMessage = {
      _id: `temp-${Date.now()}`,
      content: newMessage,
      from: user?.assignedDID?.number,
      to: formattedNumber,
      timestamp: new Date().toISOString(),
      direction: 'outbound',
      status: 'sending',
      isTemp: true
    };

    try {
      setLoading(true);
      
      // First set the selected conversation to the normalized number
      setSelectedConversation(formattedNumber);
      
      // Create a temporary conversation entry
      setConversations(prevConversations => {
        // Check if we already have this conversation (using normalized comparison)
        const exists = prevConversations.some(c => normalizeNumber(c.with) === formattedNumber);
        if (!exists) {
          // Create a new conversations array with the new conversation at the beginning
          const updatedConversations = [{
            with: formattedNumber,
            timestamp: new Date().toISOString(),
            messages: [],
            unreadCount: 0
          }, ...prevConversations];
          return updatedConversations;
        }
        return prevConversations;
      });

      // Reset new conversation form before sending
      setShowNewConversation(false);
      setNewPhoneNumber('');

      // Add the temporary message
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages, tempMessage];
        return updatedMessages;
      });
      
      // Clear the input field immediately
      setNewMessage('');
      
      // Send the message - use the API formatted number
      const response = await sms.send({
        to: apiNumber,
        content: newMessage
      });
      
      // The server returns the message directly in response.data
      // But sometimes it might return it in response.data.message format
      const messageData = response?.data?.message || response?.data;
      
      // Replace temp message with real message or mark it as sent
      if (messageData && messageData._id) {
        setMessages(prevMessages => {
          return prevMessages.map(msg => 
            msg._id === tempMessage._id ? { ...messageData, read: true } : msg
          );
        });
        
        // If we get SMS usage info in the response, update user state
        if (response.data?.smsUsage) {
          setUser(prevUser => {
            if (!prevUser) return prevUser;
            
            const updatedUser = {
              ...prevUser,
              smsUsage: response.data.smsUsage
            };
            
            return updatedUser;
          });
        }
      } else {
        // Just mark the temp message as 'sent' if no real message data
        setMessages(prevMessages => {
          return prevMessages.map(msg => 
            msg._id === tempMessage._id ? { ...msg, status: 'sent', isTemp: false } : msg
          );
        });
      }
      
      // Scroll to latest message
      setTimeout(() => scrollToBottom(true), 10);
      
    } catch (error) {
      console.error('Error in startNewConversation:', error);
      
      if (error?.response?.status === 403 && error?.response?.data?.message?.includes('SMS limit reached')) {
        handleSmsLimitError(error, tempMessage._id);
      } else if (error?.response?.status !== 500) {
        // For non-server errors that aren't limit related
        toast.error(error.response?.data?.message || 'Failed to send message');
        
        setMessages(prevMessages => {
          return prevMessages.map(msg => 
            msg.isTemp ? { ...msg, status: 'failed', isTemp: false } : msg
          );
        });
      } else {
        // For server errors
        toast.error('Server error - please try again later');
        
        setMessages(prevMessages => {
          return prevMessages.map(msg => 
            msg.isTemp ? { ...msg, status: 'failed', isTemp: false } : msg
          );
        });
      }
    } finally {
      setLoading(false);
    }
  }, [newPhoneNumber, newMessage, user, setUser, scrollToBottom, normalizeNumber, handleSmsLimitError]);

  // ====== SOCKET MANAGEMENT ======
  
  // Heartbeat to keep connection alive and detect disconnections
  const startHeartbeat = useCallback((socket) => {
    // Clear any existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Set up a new heartbeat
    heartbeatIntervalRef.current = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('client_ping', { timestamp: new Date().toISOString() });
      }
    }, 30000); // Send ping every 30 seconds
  }, []);
  
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
  }, []);


  // Handle new message from socket
  const handleSocketNewMessage = useCallback((data) => {
    console.log('Socket new_message event:', data);
    
    if (!data.message) {
      console.error('Received invalid message data:', data);
      return;
    }

    try {
      // Add message ID tracking to prevent duplicates
      const messageId = data.message._id;
      if (processedMessages.current.has(messageId)) {
        console.log('Skipping duplicate message:', messageId);
        return;
      }
      processedMessages.current.add(messageId);
      
      // Play notification if enabled and message is from other party
      if (!notificationsMuted && data.message.from !== user?.assignedDID?.number) {
        playNotificationSound();
      }

      // Remember if we were at the bottom before adding the new message
      const wasAtBottom = checkIfAtBottom();
      
      // Determine which conversation this belongs to
      // Normalize numbers for consistent comparison
      const normalizedUserDID = normalizeNumber(user?.assignedDID?.number || '');
      const normalizedFrom = normalizeNumber(data.message.from);
      const normalizedTo = normalizeNumber(data.message.to);
      
      const partner = normalizedFrom === normalizedUserDID ? normalizedTo : normalizedFrom;
      
      // Skip processing if this is our own message that we've already added to the UI
      if (data.message.from === user?.assignedDID?.number && messages.some(m => m.content === data.message.content && m.isTemp)) {
        console.log('Skipping own message that was already added to UI');
        return;
      }

      // Flag to track if this is an incoming message from someone else
      const isIncomingMessage = normalizedFrom !== normalizedUserDID;
      const normalizedSelectedConversation = normalizeNumber(selectedConversation);
      const isActiveConversation = partner === normalizedSelectedConversation;

      // Update the current conversation messages if this is for the active conversation
      if (isActiveConversation) {
        setMessages(prevMessages => {
          // Check if we already have this message or a temp version of it
          const messageExists = prevMessages.some(msg => 
            msg._id === messageId || 
            (msg.isTemp && msg.content === data.message.content)
          );
          
          if (!messageExists) {
            const newMessages = dedupeMessages([...prevMessages, data.message]).sort(
              (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
            );
            
            // Scroll to bottom if we were already at the bottom
            if (wasAtBottom) {
              setTimeout(() => scrollToBottom(true), 10);
            } else if (isIncomingMessage && !showNewMessageIndicator) {
              // Only show new message indicator for incoming messages
              setShowNewMessageIndicator(true);
              // We still need to track the unread count for the UI indicator
              setUnreadCount(prev => prev + 1);
            }
            
            return newMessages;
          }
          return prevMessages;
        });
        
        // If this is the active conversation, mark it as read immediately
        if (isIncomingMessage) {
          console.log('Marking active conversation as read immediately');
          try {
            sms.markAsRead(partner);
          } catch (markReadError) {
            console.error('Error marking messages as read:', markReadError);
          }
        }
      } else {
        // Show notification for messages from other conversations
        toast.info(`New message from ${formatPhoneNumber(partner)}`, {
          onClick: () => loadMessages(partner)
        });
      }

      // Update conversations list without reloading
      setConversations(prevConversations => {
        // Look for conversation using normalized numbers - try both formats
        const existingIndex = prevConversations.findIndex(c => 
          normalizeNumber(c.with) === partner
        );
        
        if (existingIndex >= 0) {
          const updatedConversations = [...prevConversations];
          
          // Only increment unreadCount for incoming messages (not our own messages)
          // and only when the conversation is not currently selected
          const shouldIncrementUnread = isIncomingMessage && !isActiveConversation;
          
          // We still track the count in the state, but we'll only show a red dot in the UI
          const newUnreadCount = shouldIncrementUnread ? 
            (updatedConversations[existingIndex].unreadCount || 0) + 1 : 
            (isActiveConversation ? 0 : updatedConversations[existingIndex].unreadCount || 0);
          
          updatedConversations[existingIndex] = {
            ...updatedConversations[existingIndex],
            lastMessage: data.message,
            timestamp: data.message.timestamp || new Date().toISOString(),
            unreadCount: newUnreadCount
          };
          
          // Sort conversations by timestamp
          return updatedConversations.sort((a, b) => {
            const aTime = new Date(a.timestamp || a.lastMessage?.timestamp || 0);
            const bTime = new Date(b.timestamp || b.lastMessage?.timestamp || 0);
            return bTime - aTime;
          });
        } else {
          // This is a new conversation
          // Only add unreadCount for incoming messages (not our own)
          // We'll track the count in the state but only show a red dot in the UI
          const unreadCount = isIncomingMessage && !isActiveConversation ? 1 : 0;
          
          // Add new conversation to the list
          const newConversation = {
            with: partner,
            lastMessage: data.message,
            timestamp: data.message.timestamp || new Date().toISOString(),
            unreadCount: unreadCount
          };
          
          return [newConversation, ...prevConversations].sort((a, b) => {
            const aTime = new Date(a.timestamp || a.lastMessage?.timestamp || 0);
            const bTime = new Date(b.timestamp || b.lastMessage?.timestamp || 0);
            return bTime - aTime;
          });
        }
      });
    } catch (error) {
      console.error('Error processing new message in handleSocketNewMessage:', error);
    }
  }, [user, selectedConversation, notificationsMuted, checkIfAtBottom, scrollToBottom, 
      showNewMessageIndicator, formatPhoneNumber, loadMessages, messages, normalizeNumber]);

  // Handle conversation updates - simplified to prevent duplicates
  const handleConversationUpdated = useCallback((data) => {
    console.log('Conversation updated event received:', data);
    
    if (!data || !data.partner || !data.lastMessage) {
      console.error('Invalid conversation_updated event data:', data);
      return;
    }
    
    // Normalize partner number for consistent comparison
    const normalizedPartner = normalizeNumber(data.partner);
    const normalizedSelectedConversation = normalizeNumber(selectedConversation);
    
    // Skip processing if the last message matches the last processed message
    // This prevents duplicate updates
    if (data.lastMessage._id && processedMessages.current.has(data.lastMessage._id)) {
      console.log('Skipping duplicate conversation update for message:', data.lastMessage._id);
      return;
    }
    
    // Add last message ID to processed set
    if (data.lastMessage._id) {
      processedMessages.current.add(data.lastMessage._id);
    }
    
    try {
      setConversations(prevConversations => {
        // Find conversation using normalized comparison
        const existingIndex = prevConversations.findIndex(c => normalizeNumber(c.with) === normalizedPartner);
        
        if (existingIndex >= 0) {
          const updatedConversations = [...prevConversations];
          
          // Create updated conversation object
          const updatedConversation = {
            ...updatedConversations[existingIndex]
          };
          
          // Update lastMessage if provided
          if (data.lastMessage) {
            updatedConversation.lastMessage = data.lastMessage;
            updatedConversation.timestamp = data.lastMessage.timestamp;
            
            // Only increment unreadCount for incoming messages when the conversation is not selected
            const normalizedUserDID = normalizeNumber(user?.assignedDID?.number || '');
            const messageIsFromUser = normalizeNumber(data.lastMessage.from) === normalizedUserDID;
            const shouldIncrementUnread = 
              !messageIsFromUser && 
              normalizedSelectedConversation !== normalizedPartner;
            
            if (shouldIncrementUnread) {
              updatedConversation.unreadCount = (updatedConversation.unreadCount || 0) + 1;
            } else if (normalizedSelectedConversation === normalizedPartner) {
              // If this is the selected conversation, reset unread count
              updatedConversation.unreadCount = 0;
            }
          }
          
          // If the server explicitly provides an unreadCount, use it
          if (data.unreadCount !== undefined) {
            updatedConversation.unreadCount = data.unreadCount;
          }
          
          // Update SMS usage if provided
          if (data.smsUsage && user) {
            setUser(prevUser => ({
              ...prevUser,
              smsUsage: data.smsUsage
            }));
          }
          
          // Update the conversation in the array
          updatedConversations[existingIndex] = updatedConversation;
          
          return updatedConversations.sort((a, b) => 
            new Date(b.timestamp || b.lastMessage?.timestamp || 0) - 
            new Date(a.timestamp || a.lastMessage?.timestamp || 0)
          );
        }
        
        return prevConversations;
      });
    } catch (error) {
      console.error('Error updating conversations in handleConversationUpdated:', error);
    }
  }, [messages, selectedConversation, user, setUser]);

  // Handle messages_read event from socket
  const handleMessagesRead = useCallback((data) => {
    console.log('Messages read event received:', data);
    
    if (!data || !data.partner) {
      console.error('Invalid messages_read event data:', data);
      return;
    }
    
    // Normalize partner number for consistent comparison
    const normalizedPartner = normalizeNumber(data.partner);
    const normalizedSelected = normalizeNumber(selectedConversation);
    
    // Update conversations list to reflect the new unread count
    try {
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          const normalizedConvWith = normalizeNumber(conv.with);
          
          // If this is the conversation that was marked as read
          if (normalizedConvWith === normalizedPartner) {
            // Log to see what's being updated
            console.log(`Updating conversation with ${conv.with} to unreadCount: ${data.unreadCount || 0}`);
            
            return {
              ...conv,
              unreadCount: data.unreadCount || 0
            };
          }
          return conv;
        });
        
        return updated;
      });
      
      // If this is the currently selected conversation, also update the UI indicators
      if (normalizedPartner === normalizedSelected) {
        console.log(`Resetting UI indicators for selected conversation ${selectedConversation}`);
        setShowNewMessageIndicator(false);
        setUnreadCount(0);
        
        // Also update any unread flags in the messages array
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            // If the message is from the partner and is currently unread
            if (normalizeNumber(msg.from) === normalizedPartner && !msg.read) {
              return { ...msg, read: true };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      console.error('Error updating conversations in handleMessagesRead:', error);
    }
  }, [selectedConversation]);

  // Handle message deleted event from socket
  const handleMessageDeleted = useCallback((data) => {
    console.log('Message deleted event received:', data);
    
    if (!data || !data.partner || !data.messageId) {
      console.error('Invalid message_deleted event data:', data);
      return;
    }
    
    // Normalize partner number for consistent comparison
    const normalizedPartner = normalizeNumber(data.partner);
    const normalizedSelected = normalizeNumber(selectedConversation);
    
    // Update conversations list to reflect the new unread count
    try {
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          const normalizedConvWith = normalizeNumber(conv.with);
          
          // If this is the conversation that was marked as read
          if (normalizedConvWith === normalizedPartner) {
            // Log to see what's being updated
            console.log(`Updating conversation with ${conv.with} to unreadCount: ${data.unreadCount || 0}`);
            
            return {
              ...conv,
              unreadCount: data.unreadCount || 0
            };
          }
          return conv;
        });
        
        return updated;
      });
      
      // If this is the currently selected conversation, also update the UI indicators
      if (normalizedPartner === normalizedSelected) {
        console.log(`Resetting UI indicators for selected conversation ${selectedConversation}`);
        setShowNewMessageIndicator(false);
        setUnreadCount(0);
        
        // Also update any unread flags in the messages array
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            // If the message is from the partner and is currently unread
            if (normalizeNumber(msg.from) === normalizedPartner && !msg.read) {
              return { ...msg, read: true };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      console.error('Error updating conversations in handleMessageDeleted:', error);
    }
  }, [selectedConversation]);

  // Handle conversation deleted event from socket
  const handleConversationDeleted = useCallback((data) => {
    console.log('Conversation deleted event received:', data);
    
    if (!data || !data.partner) {
      console.error('Invalid conversation_deleted event data:', data);
      return;
    }
    
    // Normalize partner number for consistent comparison
    const normalizedPartner = normalizeNumber(data.partner);
    const normalizedSelected = normalizeNumber(selectedConversation);
    
    // Update conversations list to reflect the new unread count
    try {
      setConversations(prevConversations => {
        const updated = prevConversations.filter(conv => normalizeNumber(conv.with) !== normalizedPartner);
        
        return updated;
      });
      
      // If this is the currently selected conversation, also update the UI indicators
      if (normalizedPartner === normalizedSelected) {
        console.log(`Resetting UI indicators for selected conversation ${selectedConversation}`);
        setShowNewMessageIndicator(false);
        setUnreadCount(0);
        
        // Also update any unread flags in the messages array
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            // If the message is from the partner and is currently unread
            if (normalizeNumber(msg.from) === normalizedPartner && !msg.read) {
              return { ...msg, read: true };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      console.error('Error updating conversations in handleConversationDeleted:', error);
    }
  }, [selectedConversation]);

  // Setup Socket.IO connection
  const setupSocket = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}`;
    
    console.log('Connecting to Socket.IO at:', wsUrl);
    
    // Close any existing connections
    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
      } catch (error) {
        console.error('Error disconnecting existing socket:', error);
      }
    }
    
    const socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,  // Reduced to prevent excessive reconnection attempts
      reconnectionDelay: 1000,
      timeout: 20000,
      auth: {
        token: token,
        deviceInfo: {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        }
      }
    });

    // Handle session expiration
    socket.on('session_expired', (data) => {
      console.log('Session expired:', data);
      // Prevent reconnection attempts
      socket.disconnect();
      socket.close();
      // Show message to user
      toast.warning(data.reason || 'Your session has expired. Please log in again.');
      // Logout user
      logout();
    });

    socket.on('connect', () => {
      console.log('Socket connected with ID:', socket.id);
      setSocketConnected(true);
      
      // Explicitly send authenticate event with token
      socket.emit('authenticate', token);
      
      // Start heartbeat to keep the connection alive
      startHeartbeat(socket);
    });

    socket.on('authenticated', (data) => {
      console.log('Socket authenticated successfully:', data);
      
      if (data?.user?.id) {
        console.log(`Socket authenticated for user ID: ${data.user.id}`);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setSocketConnected(false);
      
      // Stop heartbeat
      stopHeartbeat();
      
      // Show disconnection notification only if it wasn't a manual disconnect
      if (reason !== 'io client disconnect' && reason !== 'transport close') {
        // The server has disconnected us, we need to manually reconnect
        toast.warning('Real-time connection lost. Attempting to reconnect...');
        
        // Try to reconnect after a short delay
        setTimeout(() => {
          if (socket && !socket.connected) {
            console.log('Manually reconnecting socket...');
            socket.connect();
          }
        }, 2000);
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      toast.error('Failed to establish real-time connection');
    });

    socket.io.on('reconnect', (attempt) => {
      console.log(`Socket reconnected after ${attempt} attempts`);
      toast.success('Real-time connection restored');
    });

    socket.io.on('reconnect_attempt', (attempt) => {
      console.log(`Socket reconnection attempt ${attempt}`);
    });

    socket.io.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    socket.io.on('reconnect_failed', () => {
      console.error('Socket reconnection failed after all attempts');
      toast.error('Could not reconnect to server. Please refresh the page.');
    });

    return socket;
  }, [token, startHeartbeat, stopHeartbeat]);

  // Set up socket connection
  useEffect(() => {
    if (!token || !user?.assignedDID?.number) return;
    
    // Set up a new socket
    const socket = setupSocket();
    socketRef.current = socket;

    // Clean up socket connection when component unmounts
    return () => {
      stopHeartbeat();
      if (socket) {
        console.log('Socket disconnected during cleanup');
        socket.disconnect();
      }
    };
  }, [token, user, setupSocket, stopHeartbeat]);

  // Set up socket event listeners
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    
    console.log('Setting up socket event listeners');
    console.log('Current socket ID:', socket.id);
    
    // First remove any existing listeners to prevent duplicates
    socket.off('new_message');
    socket.off('conversation_updated');
    socket.off('messages_read');
    socket.off('message_deleted');
    socket.off('conversation_deleted');
    
    // Set up socket event listeners
    socket.on('new_message', handleSocketNewMessage);
    socket.on('conversation_updated', handleConversationUpdated);
    socket.on('messages_read', handleMessagesRead);
    socket.on('message_deleted', handleMessageDeleted);
    socket.on('conversation_deleted', handleConversationDeleted);
    
    // Handle server pong responses
    socket.on('server_pong', (data) => {
      if (data.clientTime) {
        const latency = Date.now() - new Date(data.clientTime).getTime();
        console.log(`Socket latency: ${latency}ms`);
      }
    });
    
    // Cleanup
    return () => {
      console.log('Cleaning up socket event listeners');
      console.log('Current socket ID during cleanup:', socket.id);
      socket.off('new_message', handleSocketNewMessage);
      socket.off('conversation_updated', handleConversationUpdated);
      socket.off('messages_read', handleMessagesRead);
      socket.off('message_deleted', handleMessageDeleted);
      socket.off('conversation_deleted', handleConversationDeleted);
      socket.off('server_pong');
    };
  }, [handleSocketNewMessage, handleConversationUpdated, handleMessagesRead, handleMessageDeleted, handleConversationDeleted, socketRef.current]);
  
  // Reset unread count and indicators when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      // Reset UI indicators
      setShowNewMessageIndicator(false);
      setUnreadCount(0);
      
      // Also reset the unreadCount in the conversations list
      setConversations(prevConversations => {
        return prevConversations.map(conv => {
          if (conv.with === selectedConversation) {
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
      });
    }
  }, [selectedConversation]);
  
  // Reconnect socket if needed when token changes
  useEffect(() => {
    if (token && !socketConnected && socketRef.current) {
      console.log('Token available but socket disconnected - attempting to reconnect');
      try {
        socketRef.current.connect();
      } catch (error) {
        console.error('Error reconnecting socket:', error);
      }
    }
  }, [token, socketConnected]);

  // ====== EFFECT HOOKS ======
  
  // Load notification preference
  useEffect(() => {
    const muted = localStorage.getItem('notificationsMuted') === 'true';
    setNotificationsMuted(muted);
  }, []);

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        await loadConversations();
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [loadConversations]);

  // Initial load - scroll to bottom without animation when conversation changes
  useEffect(() => {
    if (selectedConversation && messages.length > 0 && !messagesLoading) {
      setTimeout(() => scrollToBottom(false), 50);
    }
  }, [selectedConversation, messages.length, messagesLoading, scrollToBottom]);

  // Attach scroll listener
  useEffect(() => {
    const messageContainer = messageContainerRef.current;
    if (!messageContainer) return;
    
    const onScroll = throttledScrollHandler();
    messageContainer.addEventListener('scroll', onScroll);
    
    // Initial check
    handleScroll();
    
    return () => {
      messageContainer.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll, throttledScrollHandler]);

  // Load contacts
  const loadContacts = useCallback(async () => {
    try {
      const response = await contacts.list();
      const contactsMap = {};
      
      // Check if response.data exists and is an array
      if (response?.data && Array.isArray(response.data)) {
        response.data.forEach(contact => {
          if (contact && contact.phoneNumber) {
            // Normalize phone number for consistent lookup
            const normalizedNumber = contact.phoneNumber.replace(/\D/g, '');
            contactsMap[normalizedNumber] = contact;
          }
        });
      } else {
        console.warn('Unexpected contacts response format:', response);
      }
      
      setUserContacts(contactsMap);
    } catch (error) {
      console.error('Error loading contacts:', error);
      // Check if it's a network error
      if (!error.response) {
        toast.error('Network error while loading contacts');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load contacts');
      }
    }
  }, []);

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest('.emoji-picker-react') && !event.target.closest('button')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Function to get contact name or formatted number - with normalized lookup
  const getContactDisplay = useCallback((phoneNumber) => {
    // Normalize phone number for lookup
    const normalizedNumber = normalizeNumber(phoneNumber);
    const contact = userContacts[normalizedNumber];
    if (contact) {
      return contact.name;
    }
    return formatPhoneNumber(phoneNumber);
  }, [userContacts]);

  // Add contact modal component
  const AddContactModal = () => {
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await contacts.create({
          name,
          phoneNumber: selectedContactNumber,
          notes
        });
        await loadContacts();
        setShowContactModal(false);
        toast.success('Contact added successfully');
      } catch (error) {
        console.error('Error adding contact:', error);
        toast.error('Failed to add contact');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h2 className="text-xl font-semibold mb-4">Add New Contact</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                value={formatPhoneNumber(selectedContactNumber)}
                disabled
                className="w-full p-2 border rounded bg-gray-100"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowContactModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add Contact
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Edit contact modal component
  const EditContactModal = () => {
    const [name, setName] = useState(selectedContact?.name || '');
    const [notes, setNotes] = useState(selectedContact?.notes || '');

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        await contacts.update(selectedContact._id, {
          name,
          notes
        });
        await loadContacts();
        setShowEditContactModal(false);
        toast.success('Contact updated successfully');
      } catch (error) {
        console.error('Error updating contact:', error);
        toast.error(error.response?.data?.message || 'Failed to update contact');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h2 className="text-xl font-semibold mb-4">Edit Contact</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                type="text"
                value={formatPhoneNumber(selectedContact?.phoneNumber)}
                disabled
                className="w-full p-2 border rounded bg-gray-100"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowEditContactModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Update conversation header to include add/edit contact button
  const ConversationHeader = () => {
    const normalizedNumber = selectedConversation?.replace(/\D/g, '');
    const contact = userContacts[normalizedNumber];
    
    return (
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">
          {getContactDisplay(selectedConversation)}
        </h2>
        <div className="flex items-center space-x-3">
          {contact ? (
            <button
              onClick={() => {
                setSelectedContact(contact);
                setShowEditContactModal(true);
              }}
              className="text-blue-500 hover:text-blue-600 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit Contact
            </button>
          ) : (
            <button
              onClick={() => {
                setSelectedContactNumber(selectedConversation);
                setShowContactModal(true);
              }}
              className="text-blue-500 hover:text-blue-600 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
              Add to Contacts
            </button>
          )}
          <button
            onClick={() => setShowDeleteConversationConfirm(true)}
            className="text-red-500 hover:text-red-600 flex items-center ml-3"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Delete
          </button>
        </div>
      </div>
    );
  };

  // Update ConversationItem to use a red dot instead of showing unread count
  const ConversationItem = ({ conversation }) => {
    // Get unread count, ensuring it's a number and defaulting to 0
    const unreadCount = parseInt(conversation.unreadCount || 0, 10);
    const normalizedConvWith = normalizeNumber(conversation.with);
    const normalizedSelected = normalizeNumber(selectedConversation);
    const isSelected = normalizedConvWith === normalizedSelected;

    return (
      <div
        className={`flex items-center p-4 hover:bg-gray-100 cursor-pointer transition-colors duration-150 ${
          isSelected ? 'bg-gray-100' : ''
        }`}
        onClick={() => loadMessages(conversation.with)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center mb-1">
            <h3 className="text-sm font-medium truncate">
              {getContactDisplay(conversation.with)}
            </h3>
            <span className="text-xs text-gray-500">
              {formatTimestamp(conversation.lastMessage?.timestamp)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 truncate">
              {conversation.lastMessage?.content || 'No messages'}
            </p>
            {unreadCount > 0 && (
              <span className="bg-red-500 rounded-full h-3 w-3 min-w-3 flex-shrink-0"></span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle message deletion
  const handleDeleteMessage = useCallback(async (messageId) => {
    try {
      await sms.deleteMessage(messageId);
      
      // Update UI
      setMessages(prevMessages => prevMessages.filter(msg => msg._id !== messageId));
      
      // Close the confirmation dialog
      setMessageToDelete(null);
      setShowDeleteConfirm(false);
      
      toast.success('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  }, []);
  
  // Handle conversation deletion
  const handleDeleteConversation = useCallback(async () => {
    if (!selectedConversation) return;
    
    try {
      await sms.deleteConversation(selectedConversation);
      
      // Update UI
      setConversations(prevConversations => 
        prevConversations.filter(conv => normalizeNumber(conv.with) !== normalizeNumber(selectedConversation))
      );
      
      // Clear the selected conversation
      setSelectedConversation(null);
      setMessages([]);
      
      // Close the confirmation dialog
      setShowDeleteConversationConfirm(false);
      
      toast.success('Conversation deleted successfully');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  }, [selectedConversation]);
  
  // Delete confirmation dialog for messages
  const DeleteMessageConfirmationDialog = () => {
    if (!showDeleteConfirm || !messageToDelete) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h3 className="text-xl font-semibold mb-4">Delete Message</h3>
          <p className="mb-6">Are you sure you want to delete this message? This action cannot be undone.</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setMessageToDelete(null);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDeleteMessage(messageToDelete._id)}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Delete confirmation dialog for conversations
  const DeleteConversationConfirmationDialog = () => {
    if (!showDeleteConversationConfirm || !selectedConversation) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg w-96">
          <h3 className="text-xl font-semibold mb-4">Delete Conversation</h3>
          <p className="mb-6">
            Are you sure you want to delete the entire conversation with {formatPhoneNumber(selectedConversation)}? 
            This will delete all messages and cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConversationConfirm(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConversation}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Update the message display to include delete option
  // A colour per teammate, so at a glance you can tell who is replying in a
  // shared conversation. Stable for a given name rather than random.
  const senderColour = (name) => {
    const palette = ['#1f7a8c', '#7b4b94', '#b8621b', '#2b7a4b', '#a03e52', '#3a5a99', '#7a6a1f'];
    let hash = 0;
    for (let i = 0; i < String(name).length; i++) {
      hash = (hash * 31 + String(name).charCodeAt(i)) % 100000;
    }
    return palette[hash % palette.length];
  };

  const renderMedia = (message) => {
    if (!message.media || !message.media.length) return null;
    return (
      <div className={`flex flex-col gap-1 ${message.content ? 'mb-1' : ''}`}>
        {message.media.map((item, idx) => {
          const isImage = (item.mimeType || '').startsWith('image/');
          const isVideo = (item.mimeType || '').startsWith('video/');
          if (isImage) {
            return (
              <a key={idx} href={item.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={item.url}
                  alt={item.fileName || 'attachment'}
                  className="rounded-md max-w-full max-h-64 object-contain bg-black/5"
                  loading="lazy"
                />
              </a>
            );
          }
          if (isVideo) {
            return (
              <video key={idx} src={item.url} controls className="rounded-md max-w-full max-h-64" />
            );
          }
          return (
            <a
              key={idx}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-2 py-1 rounded bg-black/5 hover:bg-black/10 text-sm text-[#111b21]"
            >
              <i className="fas fa-paperclip text-gray-500"></i>
              <span className="truncate">{item.fileName || 'Attachment'}</span>
            </a>
          );
        })}
      </div>
    );
  };

  const renderMessages = () => {
    return messages.map((message) => {
      // Prefer the explicit direction; fall back to the number comparison for
      // any legacy record written before direction was reliable.
      const isOutbound = message.direction
        ? message.direction === 'outbound'
        : message.from === user?.assignedDID?.number;

      // Who on our side sent this. Shown on every outbound bubble so the
      // organization can tell teammates apart in a shared conversation.
      //
      // Compare only when both sides are actually present: the API returns the
      // viewer's id as `id`, so `user._id` is undefined, and an older message
      // may carry no sender at all — `undefined === undefined` would otherwise
      // attribute every unattributed message to whoever is looking at it.
      const viewerId = user?.id || user?._id || null;
      const msgSenderId = message.sender
        ? (typeof message.sender === 'object' ? message.sender._id : message.sender)
        : null;

      const sentByMe = isOutbound && (
        (viewerId && msgSenderId && String(msgSenderId) === String(viewerId)) ||
        (!!message.senderUsername && message.senderUsername === user?.username)
      );

      // Never invent a name: if the message does not say who sent it, show
      // nothing rather than guessing it was the viewer.
      const senderName = isOutbound
        ? (message.senderUsername || (sentByMe ? user?.username : null))
        : null;

      return (
      <div
        key={message._id || `msg-${message.timestamp}-${Math.random()}`}
        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-[65%] p-2 rounded-lg ${
            isOutbound
              ? (message.status === 'failed'
                  ? 'bg-red-50 border border-red-300 ml-12'
                  : sentByMe ? 'bg-[#d9fdd3] ml-12' : 'bg-[#eef7d9] ml-12')
              : 'bg-white mr-12'
          } relative group`}
        >
          {senderName && (
            <p
              className="text-[11px] font-semibold mb-0.5 leading-tight"
              style={{ color: senderColour(senderName) }}
              title={sentByMe ? 'Sent by you' : `Sent by ${senderName}`}
            >
              {sentByMe ? `${senderName} (you)` : senderName}
            </p>
          )}
          {renderMedia(message)}
          {message.content && (
            <p className="text-[#111b21] break-words">{message.content}</p>
          )}
          {isOutbound && message.status === 'failed' && (
            <p className="text-[11px] text-red-600 mt-1">
              Not delivered — the message was not sent. Send it again.
            </p>
          )}
          <p className="text-[11px] text-gray-500 text-right mt-1 flex justify-end items-center">
            {formatTimestamp(message.timestamp || message.createdAt)}
            {isOutbound && (
              message.status === 'failed' ? (
                <span className="ml-1 text-red-600 font-medium">not sent</span>
              ) : message.status === 'sending' ? (
                <span className="ml-1 text-gray-400">sending…</span>
              ) : (
                <span className="ml-1">
                  {message.status === 'delivered' ? '✓✓' : '✓'}
                </span>
              )
            )}

            {/* Delete button - visible on hover */}
            {message._id && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setMessageToDelete(message);
                  setShowDeleteConfirm(true);
                }}
                className="ml-2 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                title="Delete message"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </p>
        </div>
      </div>
      );
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!user?.assignedDID?.number) {
    return (
      <div>
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <div className="text-center">
              <i className="fas fa-phone-slash text-4xl text-red-500 mb-4"></i>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">No DID Assigned</h2>
              <p className="text-gray-600">
                You don't have a phone number assigned yet. Please contact your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#f0f2f5]">
      <Navbar />
      <div className="flex h-[calc(100vh-64px-40px)]"> {/* Reduced height to accommodate footer */}
        {/* Sidebar with Tabs */}
        <div className="w-[400px] bg-white border-r flex flex-col h-full">
          {/* Tab Navigation */}
          <div className="flex border-b relative flex-shrink-0">
            <button
              onClick={() => setShowKeyboardShortcuts(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              data-tooltip-id="keyboard-tooltip"
              data-tooltip-content='Press "?" to view keyboard shortcuts'
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 py-4 text-center font-medium transition-colors duration-200 ${
                activeTab === 'messages'
                  ? 'text-[#00a884] border-b-2 border-[#00a884]'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              data-tooltip-id="messages-tab-tooltip"
              data-tooltip-content={`Switch to Messages (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+1)`}
            >
              Messages
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 py-4 text-center font-medium transition-colors duration-200 ${
                activeTab === 'contacts'
                  ? 'text-[#00a884] border-b-2 border-[#00a884]'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              data-tooltip-id="contacts-tab-tooltip"
              data-tooltip-content={`Switch to Contacts (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+2)`}
            >
              Contacts
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'messages' ? (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="p-4 bg-[#f0f2f5] border-b flex-shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-[#41525d]">Messages</h2>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setShowNewConversation(true)}
                      className="px-4 py-2 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72] focus:outline-none"
                      data-tooltip-id="new-chat-tooltip"
                      data-tooltip-content={`Start new chat (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+T)`}
                    >
                      New Chat
                    </button>
                    <button
                      onClick={toggleNotifications}
                      className={`p-2 rounded-full hover:bg-gray-100 focus:outline-none transition-colors duration-200 ${
                        notificationsMuted ? 'text-gray-400' : 'text-[#41525d]'
                      }`}
                      data-tooltip-id="notification-tooltip"
                      data-tooltip-content={`${notificationsMuted ? "Enable" : "Disable"} notifications (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+N)`}
                    >
                      {notificationsMuted ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="relative mb-4">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={`Search messages... (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+K)`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-2 pl-10 border rounded-lg focus:outline-none focus:border-[#00a884]"
                    data-tooltip-id="search-tooltip"
                    data-tooltip-content={[
                      `${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+K to focus search`,
                      'ESC to blur and clear',
                      `${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Backspace to clear`
                    ].join('\n')}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                      aria-label="Clear search"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Add SMS Usage Bar here */}
                {user && user.role !== 'admin' && <SMSUsageBar />}
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto bg-white">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00a884]"></div>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center p-8 text-gray-500">
                    <div className="text-6xl mb-4">💬</div>
                    <p>{searchQuery ? 'No conversations found' : 'No conversations yet'}</p>
                    {!searchQuery && (
                      <button
                        onClick={() => setShowNewConversation(true)}
                        className="mt-4 text-[#00a884] hover:underline"
                      >
                        Start a new chat
                      </button>
                    )}
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <ConversationItem key={conv._id || conv.with} conversation={conv} />
                  ))
                )}
              </div>
            </div>
          ) : (
            <Suspense fallback={
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00a884]"></div>
              </div>
            }>
              <ContactsTab loadMessages={loadMessages} />
            </Suspense>
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-[#efeae2] relative">
          {showNewConversation ? (
            <div className="flex-1 bg-white">
              <div className="p-4 bg-[#f0f2f5] border-b">
                <h2 className="text-lg font-semibold text-[#41525d]">New Chat</h2>
              </div>
              <form onSubmit={startNewConversation} className="p-6 space-y-4">
                  <div>
                  <label className="block text-sm font-medium text-[#41525d] mb-1">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                    placeholder="Enter phone number"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00a884]"
                      required
                    />
                  </div>
                  <div>
                  <label className="block text-sm font-medium text-[#41525d] mb-1">
                      Message
                    </label>
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00a884]"
                      rows="4"
                      required
                  />
                  </div>
                <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowNewConversation(false)}
                    className="px-4 py-2 text-[#41525d] bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                    className="px-4 py-2 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72]"
                    >
                      Send
                    </button>
                  </div>
                </form>
            </div>
          ) : selectedConversation ? (
            <>
              {/* Chat Header */}
              <ConversationHeader />
              
              {/* Messages Area */}
              <div 
                className="flex-1 overflow-y-auto p-4 space-y-2"
                ref={messageContainerRef}
                style={{ 
                  scrollBehavior: 'smooth',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23e5e5e5' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`
                }}
              >
                {messagesLoading && hasMore && (
                  <div className="text-center p-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00a884] mx-auto"></div>
                  </div>
                )}
                {renderMessages()}
              </div>

              {/* New Message Indicator */}
              {showNewMessageIndicator && (
                <button 
                  onClick={() => scrollToBottom()} 
                  className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-white text-[#00a884] px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 hover:bg-gray-50"
                >
                  <span>New {unreadCount > 1 ? `(${unreadCount})` : ''} messages</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 1.414L8.586 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}

              {/* Message Input */}
              <div className="p-4 bg-[#f0f2f5] relative">
                {/* Staged MMS attachments */}
                {(pendingMedia.length > 0 || uploadingMedia) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {pendingMedia.map((item, idx) => (
                      <div key={idx} className="relative group">
                        {(item.mimeType || '').startsWith('image/') ? (
                          <img src={item.url} alt={item.fileName}
                               className="h-16 w-16 object-cover rounded border border-gray-300" />
                        ) : (
                          <div className="h-16 w-16 flex flex-col items-center justify-center rounded border border-gray-300 bg-white px-1">
                            <i className="fas fa-paperclip text-gray-500"></i>
                            <span className="text-[9px] text-gray-600 truncate w-full text-center">{item.fileName}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePendingMedia(idx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 text-xs leading-none flex items-center justify-center opacity-90 hover:opacity-100"
                          aria-label={`Remove ${item.fileName}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {uploadingMedia && (
                      <div className="h-16 w-16 flex items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-500">
                        {uploadProgress}%
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.vcf"
                    onChange={handleFilesPicked}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploadingMedia}
                    className="p-3 text-gray-500 hover:text-[#00a884] focus:outline-none disabled:opacity-50"
                    title="Attach a photo or file (MMS)"
                  >
                    <i className="fas fa-paperclip"></i>
                  </button>
                  <div className="relative flex-1">
                    <input
                      ref={messageInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message"
                      className="w-full p-3 bg-white rounded-lg focus:outline-none pr-12"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      😊
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={(!newMessage.trim() && pendingMedia.length === 0) || uploadingMedia}
                    className={`p-3 rounded-full ${
                      (newMessage.trim() || pendingMedia.length > 0) && !uploadingMedia
                        ? 'bg-[#00a884] text-white'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </form>
                {showEmojiPicker && (
                  <div className="absolute bottom-20 right-4 z-50">
                    <div className="emoji-picker-container" onClick={e => e.stopPropagation()}>
                      <EmojiPicker
                        onEmojiClick={(emojiObject) => {
                          console.log('Emoji clicked:', emojiObject);
                          setNewMessage(prev => prev + emojiObject.emoji);
                          setShowEmojiPicker(false);
                        }}
                        width={350}
                        height={400}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-[#f0f2f5]">
              <div className="text-center p-8">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-xl font-medium text-[#41525d] mb-2">Start Messaging</h3>
                <p className="text-gray-500 mb-4">
              Select a conversation or start a new one
                </p>
                <button
                  onClick={() => setShowNewConversation(true)}
                  className="px-6 py-3 bg-[#00a884] text-white rounded-full hover:bg-[#008f72] focus:outline-none"
                >
                  New Chat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showContactModal && (
        <AddContactModal />
      )}
      {showEditContactModal && (
        <EditContactModal />
      )}
      <DeleteMessageConfirmationDialog />
      <DeleteConversationConfirmationDialog />

      {/* Keyboard Shortcuts Modal */}
      {showKeyboardShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[480px] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Keyboard Shortcuts</h2>
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  <title>Close (ESC)</title>
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Navigation</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>Switch to Messages</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+1</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Switch to Contacts</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+2</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Previous conversation</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">↑</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Next conversation</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">↓</kbd>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium mb-2">Actions</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>New chat</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+T</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Toggle notifications</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+N</kbd>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium mb-2">Search</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>Focus search</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+K</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Clear search</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">ESC</kbd>
                  </li>
                  <li className="flex justify-between">
                    <span>Clear search input</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Backspace</kbd>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium mb-2">Help</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between">
                    <span>Show this dialog</span>
                    <kbd className="px-2 py-1 bg-gray-100 rounded">?</kbd>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <Tooltip 
        id="notification-tooltip"
        place="bottom"
        className="max-w-xs"
      />
      <Tooltip 
        id="search-tooltip" 
        place="bottom"
        className="max-w-xs whitespace-pre-line"
      />
      <Tooltip 
        id="new-chat-tooltip"
        place="bottom"
        className="max-w-xs"
      />
      <Tooltip 
        id="messages-tab-tooltip"
        place="bottom"
        className="max-w-xs"
      />
      <Tooltip 
        id="contacts-tab-tooltip"
        place="bottom"
        className="max-w-xs"
      />
      <Tooltip 
        id="keyboard-tooltip"
        place="bottom"
        className="max-w-xs"
      />
      <footer className="h-10 bg-white py-2 text-center text-gray-600 shadow-inner">
        <a 
          href="https://dial09.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-blue-600 transition-colors duration-200"
        >
          Designed and developed by dial09.com
        </a>
      </footer>
    </div>
  );
};

export default ChatWindow;
